/**
 * @file MostClickedContent.js
 * @description Card for the Most Clicked Content dashboard.
 */

import React, { useState, useEffect } from 'react';
import {
    BarChart,
    Bar,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { getMaxItemsByField } from '../../utils/utils';
import {
    DASHBOARD_MOST_CLICKED_CONTENT_MAX_ITEMS,
    DASHBOARD_MOST_CLICKED_CONTENT_BAR_SIZING,
} from './DashboardUtils';
import NotEnoughData from './NotEnoughData';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';
import './MostClickedContent.scss';
import { LogEventType } from '../../utils/data/enums';
import { getText } from '../../i18n';

/**
 * Maps API data to chart data, including type.
 * @param {Array} dataFromApi
 * @returns {Array}
 */
const mapApiResponseToMostVisitedPagesData = dataFromApi => {
    const topItems = getMaxItemsByField(
        dataFromApi,
        DASHBOARD_MOST_CLICKED_CONTENT_MAX_ITEMS,
        'count',
    );
    return topItems.map(({ name, count, eventTypeId }) => ({
        name: name || 'Unknown',
        count: count,
        eventTypeId: eventTypeId,
    }));
};

// Helper to get gradient ID based on event type
const getGradientIdForType = type => {
    if (type === LogEventType.UserVideoPlayed) return 'videoBarGradient';
    if (type === LogEventType.UserDocumentViewed) return 'documentBarGradient';
    if (type === LogEventType.UserLinkClicked) return 'linkBarGradient';
};

// Helper to render the label above each bar
const RenderContentBarLabel = props => {
    const { x, y, value } = props;
    return (
        <text x={x} y={y - 4} className="most-visited-content-bar-top-label">
            {value}
        </text>
    );
};

// Helper to render the legend dot based on content type
const RenderContentLegendDot = ({ type }) => {
    let content;
    if (type === 'VideoPlayed') {
        content = (
            <svg width="20" height="20" viewBox="0 0 12 12">
                <defs>
                    <linearGradient
                        id="videoGradient"
                        x1="0"
                        y1="6"
                        x2="12"
                        y2="6"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stopColor="#F93C65" />
                        <stop offset="1" stopColor="#F9978A" />
                    </linearGradient>
                </defs>
                <circle cx="6" cy="6" r="6" fill="url(#videoGradient)" />
            </svg>
        );
    } else if (type === 'DocumentDownloaded') {
        content = (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 12 12"
                fill="none"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z"
                    fill="url(#paint0_linear_1255_510)"
                />
                <defs>
                    <linearGradient
                        id="paint0_linear_1255_510"
                        x1="0"
                        y1="6"
                        x2="12"
                        y2="6"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stopColor="#1366FF" />
                        <stop offset="1" stopColor="#9391FC" />
                    </linearGradient>
                </defs>
            </svg>
        );
    } else if (type === 'LinkClicked') {
        content = (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 12 12"
                fill="none"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z"
                    fill="url(#paint0_linear_1255_33)"
                />
                <defs>
                    <linearGradient
                        id="paint0_linear_1255_33"
                        x1="0"
                        y1="6"
                        x2="12"
                        y2="6"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor="#3CF391" />
                        <stop offset="1" stopColor="#0ED166" />
                    </linearGradient>
                </defs>
            </svg>
        );
    }
    return content;
};

const MostClickedContent = ({ mostClickedContentData }) => {
    if (!mostClickedContentData || mostClickedContentData?.length === 0) {
        return <NotEnoughData />;
    }

    const [chartData, setChartData] = useState(mostClickedContentData);

    useEffect(() => {
        const mappedData = mapApiResponseToMostVisitedPagesData(
            mostClickedContentData,
        );
        setChartData(mappedData);
    }, [mostClickedContentData]);

    return (
        <div className="most-clicked-content-container">
            <div className="most-clicked-content-chart-row">
                <ResponsiveContainer
                    height={Math.max(
                        chartData.length *
                            DASHBOARD_MOST_CLICKED_CONTENT_BAR_SIZING,
                    )}
                >
                    <BarChart
                        layout="vertical"
                        data={chartData}
                        margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
                        barSize={16}
                        barCategoryGap={12}
                    >
                        <defs>
                            <linearGradient
                                id="videoBarGradient"
                                x1="0"
                                y1="8"
                                x2="193"
                                y2="8"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop stopColor="#F93C65" />
                                <stop offset="1" stopColor="#F9978A" />
                            </linearGradient>
                            <linearGradient
                                id="documentBarGradient"
                                x1="0"
                                y1="7.5"
                                x2="339"
                                y2="7.5"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop stopColor="#1366FF" />
                                <stop offset="1" stopColor="#9391FC" />
                            </linearGradient>
                            <linearGradient
                                id="linkBarGradient"
                                x1="8.83"
                                y1="7.5"
                                x2="159"
                                y2="7.5"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop offset="0.152598" stopColor="#3CF391" />
                                <stop offset="0.833616" stopColor="#0ED166" />
                            </linearGradient>
                        </defs>
                        <XAxis type="number" hide domain={[0, 'dataMax + 1']} />
                        <YAxis
                            dataKey="name"
                            type="category"
                            width={DASHBOARD_MOST_CLICKED_CONTENT_BAR_SIZING}
                            tick={false}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (payload && payload.length > 0) {
                                    const { name, count } = payload[0].payload;
                                    return (
                                        <CustomDashboardTooltip
                                            active={active}
                                            label={name}
                                            value={count}
                                            unit="clicks"
                                        />
                                    );
                                }
                                return null;
                            }}
                            cursor={false}
                        />
                        <Bar
                            dataKey="count"
                            radius={[2, 2, 2, 2]}
                            isAnimationActive={false}
                        >
                            {chartData.map(entry => (
                                <Cell
                                    key={entry.name}
                                    fill={`url(#${getGradientIdForType(entry.eventTypeId)})`}
                                />
                            ))}
                            <LabelList
                                dataKey="name"
                                position="top"
                                content={RenderContentBarLabel}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
                <div className="most-clicked-content-counts">
                    {chartData
                        .slice(0, DASHBOARD_MOST_CLICKED_CONTENT_MAX_ITEMS)
                        .map(item => (
                            <div
                                key={item.name}
                                className="most-clicked-content-counts-item"
                            >
                                {item.count.toLocaleString()}
                            </div>
                        ))}
                </div>
            </div>
            <div className="most-clicked-content-legend">
                <div className="legend-item">
                    <RenderContentLegendDot type="VideoPlayed" />
                    <div className="legend-label">{getText('msg_bc17c1f0179a')}</div>
                </div>
                <div className="legend-item">
                    <RenderContentLegendDot type="DocumentDownloaded" />
                    <div className="legend-label">{getText('msg_e214b8a29923')}</div>
                </div>
                <div className="legend-item">
                    <RenderContentLegendDot type="LinkClicked" />
                    <div className="legend-label">{getText('msg_d0517071aa37')}</div>
                </div>
            </div>
        </div>
    );
};
export default MostClickedContent;
